import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, X, Check } from "lucide-react";

/**
 * Pure visual component for the promo popup. Data-driven so a Phase 4 admin UI
 * can swap copy / code / link / disclaimer without code changes.
 *
 * Props (all optional, sensible defaults):
 *   open, onClose, headline, subhead, code, cta_text, cta_link, close_text,
 *   disclaimer, logo_src, reduced
 */
export default function PromoPopup({
  open,
  onClose,
  config = {},
  reduced = false,
}) {
  const dialogRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) setMounted(true);
    else {
      // Keep mounted briefly for exit transition
      const t = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const {
    headline = "Save 25% On Your First Cleaning",
    subhead = "Use code CANS25 for 25% off your first cleaning.",
    code = "CANS25",
    cta_text = "Sign Up Now",
    cta_link = "/signup?plan=monthly&promo=CANS25",
    close_text = "No thanks, I'll pay full price",
    disclaimer = "New customers only. One use per customer.",
    logo_src = "/logo-wordmark.png",
  } = config;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Fallback: select temporary textarea
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* swallow */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Promo code copied", { description: code });
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-headline"
      data-testid="promo-popup"
      data-state={open ? "open" : "closed"}
      className="fixed inset-0 z-[60] flex items-center justify-center"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close promo popup"
        onClick={onClose}
        className={`absolute inset-0 bg-slate-900/60 backdrop-blur-[2px] transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        data-testid="promo-popup-backdrop"
      />

      {/* Modal card */}
      <div
        ref={dialogRef}
        className={`relative w-full mx-4 max-w-[calc(100vw-2rem)] sm:max-w-md rounded-2xl bg-white p-7 sm:p-9 shadow-2xl transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-2 scale-[0.96]"
        } ${reduced ? "transition-opacity" : ""}`}
        data-testid="promo-popup-card"
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-2.5 right-2.5 inline-flex items-center justify-center h-11 w-11 rounded-xl text-slate-500 hover:text-ink hover:bg-slate-100 active:scale-95 transition-colors focus:outline-none focus:ring-4 focus:ring-brand/20"
          data-testid="promo-popup-close-x"
        >
          <X size={20} />
        </button>

        <div className="flex justify-center mb-5">
          <img
            src={logo_src}
            alt="Silky Smooth Trash Bin Service"
            className="h-9 sm:h-10 w-auto"
            draggable={false}
          />
        </div>

        <h2
          id="promo-headline"
          className="font-heading text-[26px] sm:text-3xl font-extrabold text-ink leading-tight text-center text-balance"
          data-testid="promo-popup-headline"
        >
          {headline}
        </h2>

        <p className="mt-3 text-[15px] sm:text-base text-slate-600 leading-relaxed text-center">
          {subhead}
        </p>

        {/* Promo code tile */}
        <button
          type="button"
          onClick={handleCopy}
          className="mt-6 w-full group relative inline-flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-brand/40 bg-brand-50/60 px-5 py-5 text-center transition-all duration-200 hover:border-brand hover:bg-brand-50 hover:-translate-y-0.5 active:scale-[0.99] focus:outline-none focus:ring-4 focus:ring-brand/25"
          data-testid="promo-popup-code-tile"
          aria-label={`Copy promo code ${code}`}
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-700 mb-1">
            Promo Code
          </span>
          <span className="font-heading text-3xl sm:text-4xl font-extrabold tracking-[0.22em] text-ink">
            {code}
          </span>
          <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 group-hover:text-brand-700 transition-colors">
            {copied ? (
              <>
                <Check size={12} className="text-accent" /> Copied!
              </>
            ) : (
              <>
                <Copy size={12} /> Tap to copy
              </>
            )}
          </span>
        </button>

        <Link
          to={cta_link}
          onClick={onClose}
          className={`mt-6 promo-cta inline-flex items-center justify-center w-full min-h-[52px] rounded-xl bg-brand text-white font-bold text-base px-6 shadow-lift transition-all duration-200 hover:bg-brand-600 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-18px_rgba(14,165,233,0.55)] active:scale-[0.99] focus:outline-none focus:ring-4 focus:ring-brand/30 ${
            reduced ? "promo-cta--no-shimmer" : ""
          }`}
          data-testid="promo-popup-cta"
        >
          {cta_text}
        </Link>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full min-h-[44px] text-sm font-medium text-slate-500 hover:text-ink transition-colors underline-offset-4 hover:underline"
          data-testid="promo-popup-close-link"
        >
          {close_text}
        </button>

        <p className="mt-4 text-xs text-slate-400 text-center" data-testid="promo-popup-disclaimer">
          {disclaimer}
        </p>
      </div>

      <style>{`
        .promo-cta {
          position: relative;
          overflow: hidden;
          background-image:
            linear-gradient(110deg,
              rgba(255,255,255,0) 0%,
              rgba(255,255,255,0) 38%,
              rgba(255,255,255,0.32) 50%,
              rgba(255,255,255,0) 62%,
              rgba(255,255,255,0) 100%
            );
          background-size: 220% 100%;
          background-repeat: no-repeat;
          background-position: -120% 0;
          animation: promo-shimmer 3.2s cubic-bezier(0.45, 0, 0.55, 1) 1.2s infinite;
          background-color: #0EA5E9;
        }
        .promo-cta:hover { background-color: #0284C7; }
        .promo-cta--no-shimmer,
        @media (prefers-reduced-motion: reduce) {
          .promo-cta { animation: none !important; background-image: none !important; }
        }
        @keyframes promo-shimmer {
          0%   { background-position: -120% 0; }
          55%  { background-position: 220% 0; }
          100% { background-position: 220% 0; }
        }
      `}</style>
    </div>
  );
}
